import React from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Card,
  CardHeader,
  CardBody,
  Divider,
  useColorModeValue,
} from '@chakra-ui/react';
import { PRM_CombinedResponse, PRM_BenchmarkResult } from '../../problem_response_models';

interface ContestTestResultsProps {
  response: PRM_CombinedResponse;
}

export default function ContestTestResults({ response }: ContestTestResultsProps) {
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const bgColor = useColorModeValue('gray.50', 'gray.800');

  // Check if we have benchmark results
  if (!response.benchmark || 'error' in response.benchmark) {
    return (
      <Alert status="error">
        <AlertIcon />
        <Box>
          <AlertTitle>Benchmark Error</AlertTitle>
          <AlertDescription>
            {response.benchmark && 'error' in response.benchmark 
              ? response.benchmark.details || 'Failed to run benchmarks'
              : 'No benchmark results available'
            }
          </AlertDescription>
        </Box>
      </Alert>
    );
  }

  const benchmark = response.benchmark;
  
  // Handle contest benchmark results (multiple inputs)
  const reference = Array.isArray(benchmark.reference) ? benchmark.reference : [benchmark.reference];
  const submission = Array.isArray(benchmark.submission) ? benchmark.submission : [benchmark.submission];

  // Calculate geometric mean average score (like the old code)
  const calculateAverageScore = (results: PRM_BenchmarkResult[]) => {
    if (results.length === 0) return 0;
    
    // Extract valid scores, defaulting to 0 for null/undefined
    const scores: number[] = [];
    for (const result of results) {
      const score = result.custom_score;
      scores.push(score !== null && score !== undefined ? score : 0);
    }
    
    // Handle zero values like the old code
    let addOne = false;
    for (let i = 0; i < scores.length; i++) {
      if (scores[i] === 0.0) {
        addOne = true;
        break;
      }
    }
    
    let logsum = 0.0;
    for (let i = 0; i < scores.length; i++) {
      const score = scores[i];
      if (typeof score === 'number') {
        logsum += Math.log(addOne ? score + 1 : score);
      }
    }
    
    const logmean = logsum / scores.length;
    let averageScore = Math.exp(logmean);
    if (addOne) {
      averageScore -= 1;
    }
    
    return averageScore;
  };

  const averageScore = calculateAverageScore(submission);

  return (
    <VStack spacing={4} align="stretch">
      {/* Benchmark Results Card */}
      <Card>
        <CardHeader bg={bgColor} borderBottom="1px" borderColor={borderColor}>
          <HStack>
            <Text>🏆</Text>
            <Text fontWeight="semibold">Benchmark Results</Text>
          </HStack>
        </CardHeader>
        <CardBody p={0}>
          <Box p={3} bg="blue.50" borderBottom="1px" borderColor={borderColor}>
            <Text fontSize="sm" color="blue.700">
              <strong>Performance Score:</strong> For BFS problems, this represents edges processed per second. 
              For SSSP problems, this represents edges processed per second. Higher scores indicate better performance.
            </Text>
          </Box>
          <Box overflowX="auto">
            <Table size="sm" variant="simple" minW="600px">
              <Thead bg="gray.100">
                <Tr>
                  <Th minW="120px">Benchmark</Th>
                  <Th minW="100px">Reference</Th>
                  <Th minW="100px">Submission</Th>
                  <Th minW="140px">Score</Th>
                </Tr>
              </Thead>
            <Tbody>
              {submission.map((subResult, index) => {
                const refResult = reference[index];
                
                if (!refResult || !subResult) {
                  return (
                    <Tr key={subResult?.benchmark_name || index}>
                      <Td colSpan={4}>
                        <Text fontSize="sm" color="gray.500">No data available</Text>
                      </Td>
                    </Tr>
                  );
                }
                
                // Format runtime values
                const formatRuntime = (runtime: number): { value: string; unit: string } => {
                  if (runtime < 0.1) {
                    return { value: (runtime * 1000).toFixed(2), unit: 'ms' };
                  } else {
                    return { value: runtime.toFixed(2), unit: 's' };
                  }
                };
                
                // Format custom score values
                const formatCustomScore = (score: number | null | undefined, name: string | null | undefined): { value: string; unit: string } => {
                  if (score === null || score === undefined) {
                    return { value: 'N/A', unit: '' };
                  }
                  
                  let value = score;
                  let unit = '';
                  
                  if (value > 1000) {
                    value /= 1000;
                    unit = 'K';
                  }
                  if (value > 1000) {
                    value /= 1000;
                    unit = 'M';
                  }
                  if (value > 1000) {
                    value /= 1000;
                    unit = 'B';
                  }
                  if (value > 1000) {
                    value /= 1000;
                    unit = 'T';
                  }
                  
                  return { 
                    value: value.toFixed(1), 
                    unit: `${unit}${name ? ` ${name}` : ''}`.trim() 
                  };
                };

                const refRuntime = formatRuntime(refResult.runtime);
                const subRuntime = formatRuntime(subResult.runtime);
                const subScore = formatCustomScore(subResult.custom_score, subResult.custom_score_name);

                return (
                  <Tr key={subResult.benchmark_name || index}>
                    <Td>
                      <Text fontSize="sm" fontWeight="medium">
                        {subResult.benchmark_name}
                      </Text>
                    </Td>
                    <Td>
                      <Text fontSize="sm">
                        {refRuntime.value} <Text as="span" fontSize="xs" color="gray.500">{refRuntime.unit}</Text>
                      </Text>
                    </Td>
                    <Td>
                      <Text fontSize="sm">
                        {subRuntime.value} <Text as="span" fontSize="xs" color="gray.500">{subRuntime.unit}</Text>
                      </Text>
                    </Td>
                    <Td>
                      <Text fontSize="sm">
                        {subScore.value} <Text as="span" fontSize="xs" color="gray.500">{subScore.unit}</Text>
                      </Text>
                    </Td>
                  </Tr>
                );
              })}
              
              {/* Average Score Row */}
              <Tr bg="green.50">
                <Td colSpan={3}>
                  <Text fontSize="md" fontWeight="bold" textAlign="center">
                    Average (geomean) score
                  </Text>
                </Td>
                <Td>
                  <Text fontSize="md" fontWeight="bold" textAlign="center">
                    {(() => {
                      const formatAverageScore = (score: number, scoreName: string | null | undefined): { value: string; unit: string } => {
                        let value = score;
                        let unit = '';
                        
                        if (value > 1000) {
                          value /= 1000;
                          unit = 'K';
                        }
                        if (value > 1000) {
                          value /= 1000;
                          unit = 'M';
                        }
                        if (value > 1000) {
                          value /= 1000;
                          unit = 'B';
                        }
                        if (value > 1000) {
                          value /= 1000;
                          unit = 'T';
                        }
                        
                        return { 
                          value: value.toFixed(1), 
                          unit: `${unit}${scoreName ? ` ${scoreName}` : ''}`.trim()
                        };
                      };
                      
                      // Get the custom score name from the first result that has one
                      const firstScoreName = submission.find(result => result.custom_score_name)?.custom_score_name;
                      const formattedScore = formatAverageScore(averageScore, firstScoreName);
                      return (
                        <>
                          {formattedScore.value} <Text as="span" fontSize="xs" color="gray.500">{formattedScore.unit}</Text>
                        </>
                      );
                    })()}
                  </Text>
                </Td>
              </Tr>
            </Tbody>
          </Table>
          </Box>
        </CardBody>
      </Card>

      {/* Correctness Tests */}
      {benchmark.tests && (
        <Card>
          <CardHeader bg={bgColor} borderBottom="1px" borderColor={borderColor}>
            <HStack>
              <Text>✅</Text>
              <Text fontWeight="semibold">Correctness Tests</Text>
            </HStack>
          </CardHeader>
          <CardBody>
            <VStack spacing={3} align="stretch">
              <HStack justify="space-between">
                <Text fontSize="sm">
                  Tests Passed: <Badge colorScheme="green">{benchmark.tests.cases_passed}</Badge>
                </Text>
                <Text fontSize="sm">
                  Tests Failed: <Badge colorScheme="red">{benchmark.tests.cases_failed}</Badge>
                </Text>
              </HStack>
              
              {benchmark.tests.test_cases && benchmark.tests.test_cases.length > 0 && (
                <VStack spacing={2} align="stretch">
                  <Text fontSize="sm" fontWeight="medium">Test Cases:</Text>
                  {benchmark.tests.test_cases.map((testCase, index) => (
                    <HStack key={index} justify="space-between" p={2} bg="gray.50" borderRadius="md">
                      <Text fontSize="sm">{testCase.name}</Text>
                      <Badge colorScheme={testCase.success ? 'green' : 'red'}>
                        {testCase.success ? 'PASS' : 'FAIL'}
                      </Badge>
                    </HStack>
                  ))}
                </VStack>
              )}
            </VStack>
          </CardBody>
        </Card>
      )}

      {/* Error Output */}
      {benchmark.stderr && (
        <Alert status="warning">
          <AlertIcon />
          <Box>
            <AlertTitle>Warning</AlertTitle>
            <AlertDescription>
              <Text fontSize="sm" fontFamily="mono" whiteSpace="pre-wrap">
                {benchmark.stderr}
              </Text>
            </AlertDescription>
          </Box>
        </Alert>
      )}
    </VStack>
  );
} 